import React, { useState, useEffect, useRef } from 'react';
import { 
  CheckCircle, 
  Clock, 
  RefreshCw, 
  Settings, 
  AlertCircle, 
  Calendar, 
  CheckSquare, 
  ListChecks, 
  Briefcase,
  Shield,
  ArrowLeftRight,
  X,
  Loader2
} from 'lucide-react';
import toast from 'react-hot-toast';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/Button';
import { 
  authenticateGoogleTasks, 
  getGoogleTasksCredentials, 
  fetchGoogleTaskLists, 
  isGoogleTasksAuthorized,
  disconnectGoogleTasks,
  syncGoogleTasks
} from '../../lib/tasks/googleTasks';

interface TaskProvider {
  id: string;
  name: string;
  icon: React.ReactNode;
  connected: boolean;
  lastSync?: Date;
  taskLists?: string[];
}

interface SyncSettings {
  frequency: 'realtime' | 'hourly' | 'daily';
  selectedTaskLists: string[];
  twoWaySync: boolean;
  conflictResolution: 'newest' | 'remote' | 'local';
}

const TasksSyncIntegration: React.FC = () => {
  const { user } = useAuth();
  const [providers, setProviders] = useState<TaskProvider[]>([
    {
      id: 'google',
      name: 'Google Tasks',
      icon: <CheckSquare className="h-6 w-6 text-red-500" />,
      connected: false,
      taskLists: []
    },
    {
      id: 'apple',
      name: 'Apple Reminders',
      icon: <CheckSquare className="h-6 w-6 text-gray-900" />,
      connected: false,
      taskLists: []
    },
    {
      id: 'microsoft',
      name: 'Microsoft To Do',
      icon: <CheckSquare className="h-6 w-6 text-blue-500" />,
      connected: false,
      taskLists: []
    },
    {
      id: 'yahoo',
      name: 'Yahoo Tasks',
      icon: <CheckSquare className="h-6 w-6 text-purple-500" />,
      connected: false,
      taskLists: []
    }
  ]);

  const [syncSettings, setSyncSettings] = useState<SyncSettings>({
    frequency: 'hourly',
    selectedTaskLists: [],
    twoWaySync: true,
    conflictResolution: 'newest'
  });
  
  const [isLoading, setIsLoading] = useState(false);
  const [isSyncing, setIsSyncing] = useState<string | null>(null);
  const [showConfigModal, setShowConfigModal] = useState<string | null>(null);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);

  // Load saved credentials on component mount
  useEffect(() => {
    if (user) {
      loadTaskCredentials();
    }
  }, [user]);

  const loadTaskCredentials = async () => {
    if (!user) return;
    
    setIsLoading(true);
    setIsCheckingAuth(true);
    
    try {
      // Check if Google Tasks is authorized
      const isGoogleAuthorized = await isGoogleTasksAuthorized();
      
      if (isGoogleAuthorized) {
        const credentials = await getGoogleTasksCredentials();
        
        if (credentials) {
          // Update Google provider with connection status
          setProviders(prev => prev.map(provider => {
            if (provider.id === 'google') {
              return {
                ...provider,
                connected: true,
                lastSync: credentials.lastSync ? new Date(credentials.lastSync) : new Date(),
                taskLists: credentials.taskLists?.map(list => list.title) || ['Default List']
              };
            }
            return provider;
          }));
          
          // Update sync settings with the Google task lists
          const googleTaskLists = credentials.taskLists?.map(list => list.title) || ['Default List'];
          setSyncSettings(prev => ({
            ...prev,
            selectedTaskLists: [...prev.selectedTaskLists, ...googleTaskLists]
          }));
        }
      }
      
      // Check other providers (mock data for demo)
      const { data, error } = await supabase
        .from('user_credentials')
        .select('provider, credentials')
        .eq('user_id', user.id)
        .in('provider', ['apple_reminders', 'microsoft_todo', 'yahoo_tasks']);
      
      if (error) throw error;
      
      if (data && data.length > 0) {
        // Update providers with connection status
        const updatedProviders = [...providers];
        
        data.forEach(credential => {
          const providerIndex = updatedProviders.findIndex(p => 
            p.id === credential.provider.replace('_reminders', '').replace('_todo', '').replace('_tasks', '')
          );
          
          if (providerIndex !== -1) {
            updatedProviders[providerIndex] = {
              ...updatedProviders[providerIndex],
              connected: true,
              lastSync: new Date(credential.credentials.lastSync || Date.now()),
              taskLists: credential.credentials.taskLists || ['Default List']
            };
          }
        });
        
        setProviders(updatedProviders);
        
        // Update sync settings with selected task lists
        const allTaskLists = updatedProviders
          .filter(p => p.connected)
          .flatMap(p => p.taskLists || []);
        
        setSyncSettings(prev => ({
          ...prev,
          selectedTaskLists: [...prev.selectedTaskLists, ...allTaskLists]
        }));
      }
    } catch (error) {
      console.error('Error loading task credentials:', error);
      toast.error('Failed to load task connections');
    } finally {
      setIsLoading(false);
      setIsCheckingAuth(false);
    }
  };

  const handleConnect = async (providerId: string) => {
    if (!user) {
      toast.error('You must be logged in to connect task providers');
      return;
    }
    
    setIsLoading(true);
    
    try {
      if (providerId === 'google') {
        // Initiate Google Tasks OAuth flow
        await authenticateGoogleTasks();
        // The rest will be handled when the user returns from the OAuth flow
        return;
      }
      
      // For other providers, simulate a successful connection
      // In a real implementation, this would initiate the appropriate OAuth flow
      
      // Simulate OAuth flow delay
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Mock task lists that would be returned from the API
      const mockTaskLists = ['Default List', 'Work', 'Personal', 'Shopping', 'Errands'];
      
      // Store the credentials in the database
      const { error } = await supabase
        .from('user_credentials')
        .upsert({
          user_id: user.id,
          provider: `${providerId}_${providerId === 'apple' ? 'reminders' : providerId === 'microsoft' ? 'todo' : 'tasks'}`,
          credentials: {
            access_token: 'mock_access_token',
            refresh_token: 'mock_refresh_token',
            expiry: new Date(Date.now() + 3600000).toISOString(),
            taskLists: mockTaskLists,
            lastSync: new Date().toISOString()
          }
        });
      
      if (error) throw error;
      
      // Update the provider in state
      setProviders(prev => prev.map(provider => {
        if (provider.id === providerId) {
          return {
            ...provider,
            connected: true,
            lastSync: new Date(),
            taskLists: mockTaskLists
          };
        }
        return provider;
      }));
      
      // Update sync settings with the new task lists
      setSyncSettings(prev => ({
        ...prev,
        selectedTaskLists: [...prev.selectedTaskLists, ...mockTaskLists]
      }));
      
      toast.success(`Connected to ${providerId} successfully`);
    } catch (error) {
      console.error(`Failed to connect to ${providerId}:`, error);
      toast.error(`Failed to connect to ${providerId}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDisconnect = async (providerId: string) => {
    if (!user) return;
    
    setIsLoading(true);
    
    try {
      if (providerId === 'google') {
        await disconnectGoogleTasks();
      } else {
        // Delete the credentials from the database for other providers
        const { error } = await supabase
          .from('user_credentials')
          .delete()
          .eq('user_id', user.id)
          .eq('provider', `${providerId}_${providerId === 'apple' ? 'reminders' : providerId === 'microsoft' ? 'todo' : 'tasks'}`);
        
        if (error) throw error;
      }
      
      // Get the task lists that will be removed
      const providerToRemove = providers.find(p => p.id === providerId);
      const taskListsToRemove = providerToRemove?.taskLists || [];
      
      // Update the provider in state
      setProviders(prev => prev.map(provider => {
        if (provider.id === providerId) {
          return {
            ...provider,
            connected: false,
            lastSync: undefined,
            taskLists: []
          };
        }
        return provider;
      }));
      
      // Remove the task lists from selected task lists
      setSyncSettings(prev => ({
        ...prev,
        selectedTaskLists: prev.selectedTaskLists.filter(list => 
          !taskListsToRemove.includes(list)
        )
      }));
      
      toast.success(`Disconnected from ${providerId}`);
    } catch (error) {
      console.error(`Failed to disconnect from ${providerId}:`, error);
      toast.error(`Failed to disconnect from ${providerId}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSync = async (providerId: string) => {
    try {
      const provider = providers.find(p => p.id === providerId);
      if (!provider?.connected) return;
      
      setIsSyncing(providerId);

      if (providerId === 'google') {
        // Get Google Tasks credentials
        const credentials = await getGoogleTasksCredentials();
        if (!credentials || !credentials.access_token) {
          throw new Error('Google Tasks credentials not found');
        }
        
        // Get the first task list for now
        // In a real implementation, you would sync all selected task lists
        const taskLists = await fetchGoogleTaskLists(credentials.access_token);
        if (taskLists.length === 0) {
          throw new Error('No task lists found');
        }
        
        // Sync tasks for the first task list
        await syncGoogleTasks(credentials.access_token, taskLists[0].id);
        
        toast.success('Google Tasks synced successfully');
      } else {
        // For other providers, simulate a successful sync
        await new Promise(resolve => setTimeout(resolve, 2000));
        toast.success(`${provider.name} synced successfully`);
      }

      // Update last sync time
      setProviders(prev => prev.map(p => {
        if (p.id === providerId) {
          return { ...p, lastSync: new Date() };
        }
        return p;
      }));
    } catch (error) {
      console.error('Sync error:', error);
      toast.error(`Failed to sync with ${providers.find(p => p.id === providerId)?.name}`);
    } finally {
      setIsSyncing(null);
    }
  };

  const handleSyncAll = async () => {
    try {
      setIsSyncing('all');

      // Sync each connected provider
      const connectedProviders = providers.filter(p => p.connected);
      
      for (const provider of connectedProviders) {
        try {
          await handleSync(provider.id);
        } catch (error) {
          console.error(`Error syncing ${provider.name}:`, error);
        }
      }

      toast.success('All task providers synced successfully');
    } catch (error) {
      console.error('Sync all error:', error);
      toast.error('Failed to sync some task providers');
    } finally {
      setIsSyncing(null);
    }
  };

  const handleTaskListToggle = (taskList: string) => {
    setSyncSettings(prev => ({
      ...prev,
      selectedTaskLists: prev.selectedTaskLists.includes(taskList)
        ? prev.selectedTaskLists.filter(t => t !== taskList)
        : [...prev.selectedTaskLists, taskList]
    }));
  };

  const handleSaveSettings = async () => {
    if (!user) return;
    
    setIsLoading(true);
    
    try {
      // In a real implementation, this would update settings in the database
      // For this demo, we'll just show a success message
      
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      toast.success('Task sync settings saved successfully');
    } catch (error) {
      console.error('Error saving task sync settings:', error);
      toast.error('Failed to save task sync settings');
    } finally {
      setIsLoading(false);
    }
  };

  const openConfigModal = (providerId: string) => {
    setShowConfigModal(providerId);
  };

  const closeConfigModal = () => {
    setShowConfigModal(null);
  };

  // Check for Google Tasks authorization on page load or after redirect
  useEffect(() => {
    const checkGoogleTasksAuth = async () => {
      // Check if we're returning from a Google OAuth redirect
      const hash = window.location.hash;
      const params = new URLSearchParams(hash.substring(1));
      const accessToken = params.get('access_token');
      
      if (accessToken && window.location.pathname.includes('/dashboard/settings')) {
        try {
          setIsLoading(true);
          
          // Get task lists
          const taskLists = await fetchGoogleTaskLists(accessToken);
          
          // Save credentials
          await saveGoogleTasksCredentials({
            access_token: accessToken,
            refresh_token: params.get('refresh_token') || undefined,
            expiry_date: params.get('expires_in') 
              ? Date.now() + parseInt(params.get('expires_in')!) * 1000 
              : undefined,
            taskLists
          });
          
          // Update provider state
          setProviders(prev => prev.map(provider => {
            if (provider.id === 'google') {
              return {
                ...provider,
                connected: true,
                lastSync: new Date(),
                taskLists: taskLists.map(list => list.title)
              };
            }
            return provider;
          }));
          
          // Update selected task lists
          setSyncSettings(prev => ({
            ...prev,
            selectedTaskLists: [
              ...prev.selectedTaskLists,
              ...taskLists.map(list => list.title)
            ]
          }));
          
          toast.success('Google Tasks connected successfully');
          
          // Clear the hash to avoid reprocessing on refresh
          window.history.replaceState(null, '', window.location.pathname);
        } catch (error) {
          console.error('Error processing Google Tasks auth callback:', error);
          toast.error('Failed to connect Google Tasks');
        } finally {
          setIsLoading(false);
        }
      }
    };
    
    checkGoogleTasksAuth();
  }, []);

  if (isCheckingAuth) {
    return (
      <div className="flex justify-center py-12">
        <div className="text-center">
          <Loader2 className="h-8 w-8 text-primary animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Checking task provider connections...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-medium text-gray-900">Tasks Sync Integration</h2>
          <p className="mt-1 text-sm text-gray-500">
            Sync your tasks across multiple platforms to manage all your to-dos in one place
          </p>
        </div>
        <div className="flex items-center gap-4">
          <select
            value={syncSettings.frequency}
            onChange={(e) => setSyncSettings(prev => ({
              ...prev,
              frequency: e.target.value as SyncSettings['frequency']
            }))}
            className="rounded-md border-gray-300 text-sm focus:ring-primary focus:border-primary"
          >
            <option value="realtime">Real-time sync</option>
            <option value="hourly">Hourly sync</option>
            <option value="daily">Daily sync</option>
          </select>
          
          <Button
            onClick={handleSyncAll}
            className="py-1.5 px-3"
            disabled={isLoading || isSyncing !== null || !providers.some(p => p.connected)}
          >
            {isSyncing === 'all' ? (
              <>
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                Syncing...
              </>
            ) : (
              <>
                <RefreshCw className="h-4 w-4 mr-2" />
                Sync All
              </>
            )}
          </Button>
          
          <Button
            onClick={handleSaveSettings}
            className="py-1.5 px-3"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              'Save Settings'
            )}
          </Button>
        </div>
      </div>

      {/* Task Providers */}
      <div className="grid grid-cols-1 gap-6">
        {providers.map((provider) => (
          <div
            key={provider.id}
            className={`bg-white rounded-lg border p-6 ${
              provider.connected ? 'border-primary' : 'border-gray-200'
            }`}
          >
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-4">
                {provider.icon}
                <div>
                  <h3 className="text-lg font-medium text-gray-900">{provider.name}</h3>
                  {provider.connected && provider.lastSync && (
                    <p className="text-sm text-gray-500">
                      Last synced: {provider.lastSync.toLocaleString()}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {provider.connected ? (
                  <>
                    <button
                      onClick={() => handleSync(provider.id)}
                      className="p-2 text-gray-400 hover:text-gray-500"
                      title="Sync now"
                      disabled={isSyncing !== null}
                    >
                      {isSyncing === provider.id ? (
                        <RefreshCw className="h-5 w-5 animate-spin text-primary" />
                      ) : (
                        <RefreshCw className="h-5 w-5" />
                      )}
                    </button>
                    <button
                      onClick={() => openConfigModal(provider.id)}
                      className="btn btn-outline py-1.5 px-3"
                    >
                      Configure
                    </button>
                    <button
                      onClick={() => handleDisconnect(provider.id)}
                      className="btn btn-outline py-1.5 px-3"
                    >
                      Disconnect
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => handleConnect(provider.id)}
                    className="btn btn-primary py-1.5 px-3"
                    disabled={isLoading}
                  >
                    {isLoading && provider.id === 'google' ? 'Connecting...' : 'Connect'}
                  </button>
                )}
              </div>
            </div>

            {provider.connected && provider.taskLists && provider.taskLists.length > 0 && (
              <div className="mt-6 space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-medium text-gray-700">Task Lists</h4>
                  <div className="flex items-center gap-4">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={syncSettings.twoWaySync}
                        onChange={(e) => setSyncSettings(prev => ({
                          ...prev,
                          twoWaySync: e.target.checked
                        }))}
                        className="rounded border-gray-300 text-primary focus:ring-primary"
                      />
                      <span className="text-sm text-gray-600">Two-way sync</span>
                    </label>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {provider.taskLists.map((taskList) => (
                    <label
                      key={taskList}
                      className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50"
                    >
                      <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          checked={syncSettings.selectedTaskLists.includes(taskList)}
                          onChange={() => handleTaskListToggle(taskList)}
                          className="rounded border-gray-300 text-primary focus:ring-primary"
                        />
                        <span className="text-sm font-medium text-gray-700">{taskList}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-500">
                          {provider.id === 'google' ? 'Google Tasks' : 
                           provider.id === 'apple' ? 'Apple Reminders' : 
                           provider.id === 'microsoft' ? 'Microsoft To Do' : 'Yahoo Tasks'}
                        </span>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Configuration Modal */}
      {showConfigModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-lg w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">
                Configure {providers.find(p => p.id === showConfigModal)?.name}
              </h3>
              <button
                onClick={closeConfigModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-6">
              {/* Task List Mapping */}
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-3">Task List Mapping</h4>
                <p className="text-sm text-gray-500 mb-3">
                  Map task lists from {providers.find(p => p.id === showConfigModal)?.name} to AdvisorGrow tasks
                </p>
                
                <div className="space-y-3">
                  {providers.find(p => p.id === showConfigModal)?.taskLists?.map((list) => (
                    <div key={list} className="flex items-center justify-between border-b pb-2">
                      <div className="flex items-center">
                        <CheckSquare className="h-4 w-4 text-gray-400 mr-2" />
                        <span className="text-sm">{list}</span>
                      </div>
                      <div>
                        <select className="text-sm border-gray-300 rounded-md focus:ring-primary focus:border-primary">
                          <option value="default">Default Tasks</option>
                          <option value="client">Client Tasks</option>
                          <option value="personal">Personal Tasks</option>
                          <option value="team">Team Tasks</option>
                        </select>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Sync Direction */}
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-3">Sync Direction</h4>
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="syncDirection"
                      checked={true}
                      className="h-4 w-4 text-primary focus:ring-primary border-gray-300"
                    />
                    <span className="ml-2 text-sm text-gray-700 flex items-center">
                      <ArrowLeftRight className="h-4 w-4 mr-1" />
                      Two-way sync (changes sync in both directions)
                    </span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="syncDirection"
                      checked={false}
                      className="h-4 w-4 text-primary focus:ring-primary border-gray-300"
                    />
                    <span className="ml-2 text-sm text-gray-700">
                      One-way: AdvisorGrow → {providers.find(p => p.id === showConfigModal)?.name}
                    </span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="syncDirection"
                      checked={false}
                      className="h-4 w-4 text-primary focus:ring-primary border-gray-300"
                    />
                    <span className="ml-2 text-sm text-gray-700">
                      One-way: {providers.find(p => p.id === showConfigModal)?.name} → AdvisorGrow
                    </span>
                  </label>
                </div>
              </div>

              {/* Field Mapping */}
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-3">Field Mapping</h4>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Title</span>
                    <span className="text-sm text-gray-600">→</span>
                    <span className="text-sm text-gray-600">Title</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Due Date</span>
                    <span className="text-sm text-gray-600">→</span>
                    <span className="text-sm text-gray-600">Due Date</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Notes</span>
                    <span className="text-sm text-gray-600">→</span>
                    <span className="text-sm text-gray-600">Description</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Priority</span>
                    <span className="text-sm text-gray-600">→</span>
                    <select className="text-xs border-gray-300 rounded-md focus:ring-primary focus:border-primary">
                      <option value="priority">Priority</option>
                      <option value="tags">Tags</option>
                      <option value="none">Don't sync</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={closeConfigModal}
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => {
                    toast.success('Configuration saved');
                    closeConfigModal();
                  }}
                >
                  Save Configuration
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Task Sync Status */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
          <Clock className="h-5 w-5 text-primary mr-2" />
          Sync Status
        </h3>
        
        <div className="space-y-4">
          {providers.map((provider) => (
            <div key={provider.id} className="flex items-center justify-between border-b pb-3">
              <div className="flex items-center">
                {provider.icon}
                <span className="ml-2 font-medium">{provider.name}</span>
              </div>
              <div className="flex items-center">
                {provider.connected ? (
                  <>
                    <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                    <span className="text-sm text-gray-600">
                      Last sync: {provider.lastSync ? provider.lastSync.toLocaleTimeString() : 'Never'}
                    </span>
                  </>
                ) : (
                  <>
                    <AlertCircle className="h-5 w-5 text-gray-400 mr-2" />
                    <span className="text-sm text-gray-500">Not connected</span>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
        
        <div className="mt-4 pt-4 border-t">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-sm font-medium text-gray-700">Next Scheduled Sync</h4>
              <p className="text-sm text-gray-500">
                {syncSettings.frequency === 'realtime' 
                  ? 'Real-time sync enabled' 
                  : syncSettings.frequency === 'hourly'
                  ? `Next sync at ${new Date(Math.ceil(Date.now() / 3600000) * 3600000).toLocaleTimeString()}`
                  : `Next sync at midnight`}
              </p>
            </div>
            <Button
              variant="outline"
              onClick={handleSyncAll}
              disabled={isSyncing !== null || !providers.some(p => p.connected)}
              className="flex items-center"
            >
              {isSyncing === 'all' ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Syncing...
                </>
              ) : (
                <>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Sync Now
                </>
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Task Integration Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start">
          <Briefcase className="h-5 w-5 text-blue-500 mt-0.5 mr-3 flex-shrink-0" />
          <div>
            <h4 className="text-sm font-medium text-blue-800">About Task Synchronization</h4>
            <p className="mt-1 text-sm text-blue-700">
              Task synchronization allows you to keep your tasks in sync across multiple platforms. 
              Changes made in AdvisorGrow will be reflected in your connected task providers and vice versa.
              This helps you manage client-related tasks alongside your personal to-dos in one unified system.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TasksSyncIntegration;